/* eslint-disable id-length */
/**
 * Defines the rules for classifying shapes as 'postform'.
 * - BACKSPLASH_HEIGHT: The required height of the backsplash in inches.
 * - EDGE_LENGTHS: The allowed lengths of edges in inches for postform classification.
*/
const POSTFORM_RULES = {
  BACKSPLASH_HEIGHT: 4,
  EDGE_LENGTHS: [25, 26],
};

const partnerProductId = typeof window !== 'undefined' && window.injectedConfiguratorSettings
  ? window.injectedConfiguratorSettings.partnerProductId
  : null;

const laminatePostFormFeatureGate =
  typeof window !== 'undefined' && window.injectedConfiguratorSettings
    ? window.injectedConfiguratorSettings.laminatePostFormFeatureGate
    : undefined;
/**
 * Determines the overall design classification based on the classifications of individual shapes.
 * If any shape is classified as 'custom', the overall design is classified as 'custom'.
 * Otherwise, the design is classified as 'postform'.
 *
 * @param {Array} classifiedShapes - An array of shape objects with a `classification` property.
 * @returns {string} - The overall design classification ('custom' or 'postform').
 */
export const classifyDesign = (classifiedShapes) => {

  const isCustom = classifiedShapes.some((shape) => shape.classification === 'custom');

  return isCustom ? 'custom' : 'postform';
};

/**
 * Classifies a rectangle shape based on Post Form criteria.
 * Criteria:
 * - One edge must be 25" or 26" long.
 * - One adjacent edge must be against a wall and have a 4" backsplash.
 * - The edge opposite the backsplash cannot be against a wall.
 *
 * @param {Object} shape - The rectangle shape object with edge properties.
 * @returns {Object} - The shape object with a classification of 'postform' or 'custom'.
 */
export const classifyRectangle = (shape) => {
  const { edges } = shape;

  const hasInvalidBacksplashHeight = Object.values(edges).some((edge) =>
    edge.hasBacksplash && edge.backsplashHeight !== POSTFORM_RULES.BACKSPLASH_HEIGHT);

  if (hasInvalidBacksplashHeight) {
    return {
      ...shape,
      classification: 'custom',
    };
  }

  const isPostForm = (primaryEdge, adjacentEdge, adjacentOppositeEdge) => (
    POSTFORM_RULES.EDGE_LENGTHS.includes(edges[primaryEdge].length) &&
    edges.a.length === edges.c.length &&
    edges.b.length === edges.d.length &&
    edges[adjacentEdge].isAgainstWall &&
    !edges[adjacentOppositeEdge].isAgainstWall &&
    edges[adjacentEdge].hasBacksplash
  );

  if (
    isPostForm('a', 'b', 'd') ||
    isPostForm('a', 'd', 'b') ||
    isPostForm('c', 'b', 'd') ||
    isPostForm('c', 'd', 'b') ||
    isPostForm('b', 'c', 'a') ||
    isPostForm('b', 'a', 'c') ||
    isPostForm('d', 'a', 'c') ||
    isPostForm('d', 'c', 'a')
  ) {
    return {
      ...shape,
      classification: 'postform',
    };
  }

  return {
    ...shape,
    classification: 'custom',
  };
};

/**
 * Classifies an L-shape based on Post Form criteria.
 * Criteria:
 * - Sides A and D must be 25" or 26" and equal in length.
 * - Sides B and C must be against a wall and have a 4" backsplash.
 *
 * @param {Object} shape - The L-shape object with edge properties.
 * @returns {Object} - The shape object with a classification of 'postform' or 'custom'.
 */
export const classifyLShape = (shape) => {
  const { edges } = shape;

  const hasInvalidBacksplashHeight = Object.values(edges).some((edge) =>
    edge.hasBacksplash && edge.backsplashHeight !== POSTFORM_RULES.BACKSPLASH_HEIGHT);

  if (hasInvalidBacksplashHeight) {
    return {
      ...shape,
      classification: 'custom',
    };
  }

  const isPostForm = () => (
    edges.a.length === edges.d.length &&
    POSTFORM_RULES.EDGE_LENGTHS.includes(edges.a.length) &&
    edges.b.isAgainstWall &&
    edges.c.isAgainstWall &&
    edges.b.hasBacksplash &&
    edges.c.hasBacksplash
  );

  if (isPostForm()) {
    return {
      ...shape,
      classification: 'postform',
    };
  }

  return {
    ...shape,
    classification: 'custom',
  };
};

/**
 * Classifies a U-shape based on Post Form criteria.
 * Criteria:
 * - Sides A and E must be 25" or 26" and equal in length.
 * - Sides B, C, and D must be against a wall and have a 4" backsplash.
 * - Side D minus Side F must equal Side A.
 *
 * @param {Object} shape - The U-shape object with edge properties.
 * @returns {Object} - The shape object with a classification of 'postform' or 'custom'.
 */
export const classifyUShape = (shape) => {
  const { edges } = shape;

  const hasInvalidBacksplashHeight = Object.values(edges).some((edge) =>
    edge.hasBacksplash && edge.backsplashHeight !== POSTFORM_RULES.BACKSPLASH_HEIGHT);

  if (hasInvalidBacksplashHeight) {
    return {
      ...shape,
      classification: 'custom',
    };
  }

  const isPostForm = () => (
    edges.a.length === edges.e.length &&
    POSTFORM_RULES.EDGE_LENGTHS.includes(edges.a.length) &&
    edges.b.isAgainstWall &&
    edges.c.isAgainstWall &&
    edges.d.isAgainstWall &&
    edges.b.hasBacksplash &&
    edges.c.hasBacksplash &&
    edges.d.hasBacksplash &&
    POSTFORM_RULES.EDGE_LENGTHS.includes(edges.d.length - edges.f.length)
  );

  if (isPostForm()) {
    return {
      ...shape,
      classification: 'postform',
    };
  }

  return {
    ...shape,
    classification: 'custom',
  };
};

/**
 * Classifies the edges of a shape based on certain rules.
 *
 * @param {Object} shape - The shape object containing the edges to be classified.
 * @returns {Object} - The updated shape object with classified edges.
 */
export const classifyEdges = (shape) => {

  const resetAllIsEndSplash = () => {
    const { edges } = shape;
    const classifiedEdges = { ...edges };
    Object.keys(classifiedEdges).forEach((key) => {
      classifiedEdges[key].isEndSplash = false;
    });
    return {
      ...shape,
      edges: classifiedEdges,
    };
  };

  const classify = () => {
    const { edges } = shape;
    const classifiedEdges = { ...edges };
    Object.keys(classifiedEdges).forEach((key) => {
      const isEndSplash = classifiedEdges[key].hasBacksplash &&
      classifiedEdges[key].backsplashHeight === POSTFORM_RULES.BACKSPLASH_HEIGHT &&
      POSTFORM_RULES.EDGE_LENGTHS.includes(classifiedEdges[key].length);

      classifiedEdges[key].isEndSplash = isEndSplash;
    });
    return {
      ...shape,
      edges: classifiedEdges,
    };
  };

  if (shape.classification === 'postform') {
    return classify();
  }
  return resetAllIsEndSplash();

};

/**
 * Classifies an array of shapes based on their type and Post Form criteria.
 * Each shape is classified as either 'postform' or 'custom' depending on its edges and properties.
 *
 * Supported shape types:
 * - 'rectangle': Classified using `classifyRectangle`.
 * - 'lshape': Classified using `classifyLShape`.
 * - 'ushape': Classified using `classifyUShape`.
 *
 * If the shape type is not recognized, it defaults to 'custom'.
 *
 * @param {Array} shapes - An array of shape objects to classify.
 * @returns {Array} - An array of classified shape objects with a `classification` property.
 */
export const classifyShapes = (shapes) => {

  if (partnerProductId !== 310171055 || !laminatePostFormFeatureGate) {
    return shapes.map((shape) => ({
      ...shape,
      classification: 'none',
    }));
  }

  const classifiedShapes = shapes.map((shape) => {
    switch (shape.type) {
    case 'rectangle':
      return classifyRectangle(shape);
    case 'lshape':
      return classifyLShape(shape);
    case 'ushape':
      return classifyUShape(shape);
    default:
      return {
        ...shape,
        classification: 'custom',
      };
    }
  });
  return classifiedShapes.map((shape) => classifyEdges(shape));
};
